'use client';

/**
 * Client-side affiliate click tracking.
 *
 * - Product: catalog or product/`?utm_affiliate=<AffiliateLink.shortCode>` → trackAffiliateClick
 * - List page open: `/social/.../lists/{listCode}` → trackAffiliateListClick (clicks only)
 * - Profile page open: `/social/{profileKey}` → trackAffiliateProfileClick (clicks only)
 *
 * Checkout attribution uses product AffiliateLink codes only.
 */

import {
  affiliateClickGuardKey,
  affiliateListClickGuardKey,
  affiliateProfileClickGuardKey,
  normalizeAffiliateShortCode,
} from '@/lib/affiliateLinkCode';

const isBrowser = typeof window !== 'undefined';
const VISITOR_STORAGE_KEY = 'vupt-affiliate-visitor-v1';

function getOrCreateVisitorKey(): string | null {
  if (!isBrowser) return null;
  try {
    const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY)?.trim();
    if (existing && existing.length >= 8 && existing.length <= 128) {
      return existing;
    }
    const created =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(VISITOR_STORAGE_KEY, created);
    return created;
  } catch {
    return null;
  }
}

export function trackAffiliateClick(
  storeId: string,
  rawUtmAffiliate: string | null | undefined
): void {
  if (!isBrowser || !storeId) return;
  const utmAffiliate = normalizeAffiliateShortCode(rawUtmAffiliate);
  if (!utmAffiliate) return;

  const visitorKey = getOrCreateVisitorKey();
  if (!visitorKey) return;

  const guardKey = affiliateClickGuardKey(storeId, utmAffiliate);
  try {
    if (window.sessionStorage.getItem(guardKey)) return;
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    /* no sessionStorage — still fire; server dedupes by visitorKey */
  }

  void fetch('/api/track/affiliate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ storeId, utmAffiliate, visitorKey }),
    keepalive: true,
  }).catch(() => {
    /* tracking must never break the journey */
  });
}

/** Record a unique visitor open of a public affiliate list page. */
export function trackAffiliateListClick(
  rawListShortCode: string | null | undefined
): void {
  if (!isBrowser) return;
  const listShortCode = normalizeAffiliateShortCode(rawListShortCode);
  if (!listShortCode) return;

  const visitorKey = getOrCreateVisitorKey();
  if (!visitorKey) return;

  const guardKey = affiliateListClickGuardKey(listShortCode);
  try {
    if (window.sessionStorage.getItem(guardKey)) return;
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    /* no sessionStorage — still fire; server dedupes by visitorKey */
  }

  void fetch('/api/track/affiliate-list', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listShortCode, visitorKey }),
    keepalive: true,
  }).catch(() => {
    /* tracking must never break the journey */
  });
}

/** Record a unique visitor open of a public affiliate profile page. */
export function trackAffiliateProfileClick(
  rawProfileKey: string | null | undefined
): void {
  if (!isBrowser) return;
  const profileKey = normalizeAffiliateShortCode(rawProfileKey);
  if (!profileKey) return;

  const visitorKey = getOrCreateVisitorKey();
  if (!visitorKey) return;

  const guardKey = affiliateProfileClickGuardKey(profileKey);
  try {
    if (window.sessionStorage.getItem(guardKey)) return;
    window.sessionStorage.setItem(guardKey, '1');
  } catch {
    /* no sessionStorage — still fire; server dedupes by visitorKey */
  }

  void fetch('/api/track/affiliate-profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ profileKey, visitorKey }),
    keepalive: true,
  }).catch(() => {
    /* tracking must never break the journey */
  });
}
